import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DISTRITOS, DISTRITOS_BARRIOS, CONSELLS_SECTORIALS, CONSELLS_DISTRICTE, TEMAS, FRANJAS_HORARIAS, DIES_SEMANA, VOLUNTARI_ACTES, normalizeDate } from "@/data/terrassa";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Trash2, Upload, FileText, ImageIcon, Eye } from "lucide-react";

// Valor centinela usado en los Selects para representar "sin asignar"
// (Radix Select no admite value="" en sus SelectItem)
const NONE = "__none__";

const empty = {
  nombre: "",
  apellidos: "",
  dni: "",
  direccion: "",
  telefono: "",
  email: "",
  es_apoderado: false,
  anios_apoderado: [],
  fecha_baja_apoderado: "",
  es_afiliado: false,
  permite_publicidad: false,
  permite_whatsapp: false,
  distrito: "",
  barrio: "",
  numero_afiliado: "",
  fecha_alta_afiliado: "",
  fecha_baja_afiliado: "",
  iban: "",
  tema: [],
  cumpleanos: "",
  comentaris: "",
  instagram: "",
  facebook: "",
  es_referente_territorio: false,
  es_voluntario: false,
  voluntari_disponibilitat: [],
  voluntari_horari_observacions: "",
  voluntari_equip: "",
  voluntari_actes: [],
  pertenece_consell_sectorial: false,
  consells_sectorials: [],
  pertenece_consell_districte: false,
  consells_districte: [],
};

const ANIOS_APODERADO = [2019, 2023, 2027];

// Normaliza un contacto recibido del backend: limpia fechas sentinela (ej. "0001-01-01")
const sanitizeIncoming = (c) => ({
  ...c,
  fecha_baja_apoderado: normalizeDate(c.fecha_baja_apoderado),
  fecha_baja_afiliado: normalizeDate(c.fecha_baja_afiliado),
  fecha_alta_afiliado: normalizeDate(c.fecha_alta_afiliado),
  // Coerce tema a array (por si algún registro antiguo lo guardó como string)
  tema: Array.isArray(c.tema) ? c.tema : (c.tema ? [c.tema] : []),
  voluntari_disponibilitat: Array.isArray(c.voluntari_disponibilitat) ? c.voluntari_disponibilitat : [],
  voluntari_actes: Array.isArray(c.voluntari_actes) ? c.voluntari_actes : [],
});

export default function ContactForm({ open, onClose, onSubmit, initial }) {
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...empty, ...sanitizeIncoming(initial) } : empty);
      setError("");
    }
  }, [open, initial]);

  const barrios = useMemo(
    () => (form.distrito ? DISTRITOS_BARRIOS[form.distrito] : []),
    [form.distrito]
  );

  const update = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      // Enforce: WhatsApp only allowed when publicidad is ON
      if (Object.prototype.hasOwnProperty.call(patch, "permite_publicidad") && !patch.permite_publicidad) {
        next.permite_whatsapp = false;
      }
      // Reset barrio when distrito changes
      if (Object.prototype.hasOwnProperty.call(patch, "distrito")) {
        if (!DISTRITOS_BARRIOS[patch.distrito]?.includes(prev.barrio)) {
          next.barrio = "";
        }
      }
      // Limpia los datos de afiliación SOLO si tras el cambio no es afiliado ni hay fecha de baja.
      if (!next.es_afiliado && !next.fecha_baja_afiliado) {
        next.numero_afiliado = "";
        next.fecha_alta_afiliado = "";
        next.fecha_baja_afiliado = "";
        next.iban = "";
      }
      // Limpia los datos de apoderado si ya no aplica
      if (!next.es_apoderado && !next.fecha_baja_apoderado) {
        next.anios_apoderado = [];
        next.fecha_baja_apoderado = "";
      }
      return next;
    });
  };

  const toggleAnioApoderado = (anio) => {
    setForm((prev) => {
      const set = new Set(prev.anios_apoderado || []);
      if (set.has(anio)) set.delete(anio); else set.add(anio);
      return { ...prev, anios_apoderado: Array.from(set).sort() };
    });
  };

  const toggleConsellSectorial = (name) => {
    setForm((prev) => {
      const set = new Set(prev.consells_sectorials || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...prev, consells_sectorials: [...set] };
    });
  };

  const toggleConsellDistricte = (name) => {
    setForm((prev) => {
      const set = new Set(prev.consells_districte || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...prev, consells_districte: [...set] };
    });
  };

  const toggleTema = (name) => {
    setForm((prev) => {
      const set = new Set(prev.tema || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...prev, tema: [...set] };
    });
  };

  const toggleDisponibilitat = (slot) => {
    setForm((prev) => {
      const set = new Set(prev.voluntari_disponibilitat || []);
      if (set.has(slot)) set.delete(slot); else set.add(slot);
      return { ...prev, voluntari_disponibilitat: [...set] };
    });
  };

  const toggleActe = (name) => {
    setForm((prev) => {
      const set = new Set(prev.voluntari_actes || []);
      if (set.has(name)) set.delete(name); else set.add(name);
      return { ...prev, voluntari_actes: [...set] };
    });
  };

  // ============== Ficheros (voluntario + legales) ==============
  const [voluntarioFile, setVoluntarioFile] = useState(initial?.voluntario_file || null);
  const [docsLegales, setDocsLegales] = useState(initial?.documentos_legales || []);
  const [uploadingKind, setUploadingKind] = useState(null);

  useEffect(() => {
    if (open) {
      setVoluntarioFile(initial?.voluntario_file || null);
      setDocsLegales(initial?.documentos_legales || []);
      setUploadingKind(null);
    }
  }, [open, initial]);

  const contactId = initial?.id || null;

  const uploadFile = async (kind, file, description = "") => {
    if (!contactId) {
      toast.error("Guarda primero el contacto antes de subir ficheros");
      return null;
    }
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("description", description);
      fd.append("file", file);
      const { data } = await api.post(`/contactos/${contactId}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "voluntario") setVoluntarioFile(data);
      else setDocsLegales((prev) => [...prev, data]);
      toast.success(`Fichero subido: ${data.filename}`);
      return data;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir el fichero");
      return null;
    } finally {
      setUploadingKind(null);
    }
  };

  const removeFile = async (fileId, kind) => {
    try {
      await api.delete(`/files/${fileId}`);
      if (kind === "voluntario") setVoluntarioFile(null);
      else setDocsLegales((prev) => prev.filter((f) => f.id !== fileId));
      toast.success("Fichero eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al eliminar");
    }
  };

  const updateDocDescription = async (fileId, description) => {
    setDocsLegales((prev) => prev.map((f) => f.id === fileId ? { ...f, description } : f));
    try {
      await api.patch(`/files/${fileId}`, { description });
    } catch (_err) {
      toast.error("No se pudo guardar la descripción");
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const previewUrl = (fileId) => `${backendUrl}/api/files/${fileId}/preview`;
  const downloadUrl = (fileId) => `${backendUrl}/api/files/${fileId}`;


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nombre.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }
    // Validación de email solo si se ha escrito uno
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("El email no tiene un formato válido.");
      return;
    }
    // Validación de IBAN solo si está marcado como afiliado y se ha escrito algo
    if (form.es_afiliado && form.iban) {
      const clean = form.iban.replace(/\s+/g, "").toUpperCase();
      if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(clean)) {
        setError("El IBAN no tiene un formato válido.");
        return;
      }
    }
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl rounded-sm border-slate-200 max-h-[92vh] overflow-y-auto"
        data-testid="contact-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">
            {initial ? "Editar contacto" : "Nuevo contacto"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre *">
              <Input
                value={form.nombre}
                onChange={(e) => update({ nombre: e.target.value })}
                className="rounded-sm border-slate-300"
                data-testid="form-nombre-input"
                required
              />
            </Field>
            <Field label="Apellidos">
              <Input
                value={form.apellidos}
                onChange={(e) => update({ apellidos: e.target.value })}
                className="rounded-sm border-slate-300"
                data-testid="form-apellidos-input"
              />
            </Field>
            <Field label="DNI">
              <Input
                value={form.dni}
                onChange={(e) => update({ dni: e.target.value.toUpperCase() })}
                className="rounded-sm border-slate-300 font-mono"
                data-testid="form-dni-input"
              />
            </Field>
            <Field label="Email">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => update({ email: e.target.value })}
                className="rounded-sm border-slate-300"
                data-testid="form-email-input"
              />
            </Field>
            <Field label="Teléfono">
              <Input
                value={form.telefono}
                onChange={(e) => update({ telefono: e.target.value })}
                className="rounded-sm border-slate-300"
                data-testid="form-telefono-input"
              />
            </Field>
            <Field label="Dirección">
              <Input
                value={form.direccion}
                onChange={(e) => update({ direccion: e.target.value })}
                className="rounded-sm border-slate-300"
                data-testid="form-direccion-input"
              />
            </Field>

            <Field label="Cumpleaños">
              <Input
                value={form.cumpleanos}
                onChange={(e) => update({ cumpleanos: e.target.value })}
                className="rounded-sm border-slate-300"
                placeholder="Ej.: 1980-05-12 o 12/05/1980"
                data-testid="form-cumpleanos-input"
              />
            </Field>

            <Field label="Distrito">
              <Select
                value={form.distrito ? form.distrito : NONE}
                onValueChange={(v) => update({ distrito: v === NONE ? "" : v })}
              >
                <SelectTrigger
                  className="rounded-sm border-slate-300"
                  data-testid="form-distrito-select"
                >
                  <SelectValue placeholder="Sin asignar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Sin asignar —</SelectItem>
                  {DISTRITOS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Barrio">
              <Select
                value={form.barrio ? form.barrio : NONE}
                onValueChange={(v) => update({ barrio: v === NONE ? "" : v })}
                disabled={!form.distrito}
              >
                <SelectTrigger
                  className="rounded-sm border-slate-300 disabled:opacity-50"
                  data-testid="form-barrio-select"
                >
                  <SelectValue
                    placeholder={
                      form.distrito ? "Sin asignar" : "Selecciona primero un distrito"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Sin asignar —</SelectItem>
                  {barrios.map((b) => (
                    <SelectItem key={b} value={b}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Tema (temáticas)" hint={form.tema_original ? `Original: ${form.tema_original}` : undefined}>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 border border-slate-300 rounded-sm p-2 max-h-40 overflow-y-auto" data-testid="form-tema-multiselect">
                {TEMAS.map((t) => (
                  <label key={t} className="flex items-center gap-2 cursor-pointer px-2 py-0.5 hover:bg-slate-50 rounded-sm">
                    <Checkbox
                      checked={form.tema?.includes(t)}
                      onCheckedChange={() => toggleTema(t)}
                      className="data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                      data-testid={`form-tema-${t}`}
                    />
                    <span className="text-xs text-slate-800">{t}</span>
                  </label>
                ))}
              </div>
            </Field>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
              Vínculo con el partido y consentimientos
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <BoolField
                label="¿Es apoderado del partido?"
                checked={form.es_apoderado}
                onChange={(v) => update({ es_apoderado: v })}
                testid="form-apoderado-check"
              />
              <BoolField
                label="¿Es afiliado al partido?"
                checked={form.es_afiliado}
                onChange={(v) => update({ es_afiliado: v })}
                testid="form-afiliado-check"
              />
              <BoolField
                label="¿Permite envío de publicidad?"
                checked={form.permite_publicidad}
                onChange={(v) => update({ permite_publicidad: v })}
                testid="form-publicidad-check"
              />
              <BoolField
                label="¿Permite publicidad por WhatsApp?"
                checked={form.permite_whatsapp}
                onChange={(v) => update({ permite_whatsapp: v })}
                disabled={!form.permite_publicidad}
                testid="form-whatsapp-check"
                hint={!form.permite_publicidad ? "Requiere consentimiento general" : null}
              />
            </div>

            {(form.es_apoderado || form.fecha_baja_apoderado) && (
              <div className="mt-3 p-3 border border-slate-200 bg-slate-50/50 rounded-sm space-y-3">
                {form.fecha_baja_apoderado && !form.es_apoderado && (
                  <p className="text-xs uppercase tracking-wider text-orange-700 font-semibold">
                    Apoderado en estado: Baja
                  </p>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                    Años como apoderado
                  </Label>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {ANIOS_APODERADO.map((anio) => (
                      <label
                        key={anio}
                        className="flex items-center gap-2 px-3 py-1.5 border border-slate-300 cursor-pointer hover:bg-slate-50 rounded-sm bg-white"
                      >
                        <Checkbox
                          checked={form.anios_apoderado?.includes(anio) || false}
                          onCheckedChange={() => toggleAnioApoderado(anio)}
                          className="data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                          data-testid={`form-anio-apoderado-${anio}`}
                        />
                        <span className="text-sm text-slate-900 font-mono">{anio}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                    Fecha de baja como apoderado
                  </Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      type="date"
                      value={form.fecha_baja_apoderado || ""}
                      onChange={(e) => update({ fecha_baja_apoderado: e.target.value })}
                      className="rounded-sm border-slate-300 max-w-xs"
                      data-testid="form-fecha-baja-apoderado-input"
                    />
                    {form.fecha_baja_apoderado && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => update({ fecha_baja_apoderado: "" })}
                        className="rounded-sm border-slate-300 h-9 text-xs"
                        data-testid="form-clear-fecha-baja-apoderado"
                        title="Borrar fecha"
                      >
                        <X className="w-3.5 h-3.5 mr-1" />
                        Borrar fecha
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {(form.es_afiliado || form.fecha_baja_afiliado) && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 border border-slate-200 bg-slate-50/50 rounded-sm">
                {form.fecha_baja_afiliado && !form.es_afiliado && (
                  <p className="sm:col-span-2 text-xs uppercase tracking-wider text-orange-700 font-semibold">
                    Afiliado en estado: Baja
                  </p>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                    Número de afiliado
                  </Label>
                  <Input
                    value={form.numero_afiliado}
                    onChange={(e) => update({ numero_afiliado: e.target.value })}
                    className="rounded-sm border-slate-300 mt-1.5 font-mono"
                    placeholder="Ej.: 12345"
                    data-testid="form-numero-afiliado-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                    IBAN
                  </Label>
                  <Input
                    value={form.iban}
                    onChange={(e) => update({ iban: e.target.value.toUpperCase() })}
                    className="rounded-sm border-slate-300 mt-1.5 font-mono"
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    data-testid="form-iban-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                    Fecha de alta como afiliado
                  </Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      type="date"
                      value={form.fecha_alta_afiliado || ""}
                      onChange={(e) => update({ fecha_alta_afiliado: e.target.value })}
                      className="rounded-sm border-slate-300"
                      data-testid="form-fecha-alta-input"
                    />
                    {form.fecha_alta_afiliado && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => update({ fecha_alta_afiliado: "" })}
                        className="rounded-sm border-slate-300 h-9 text-xs"
                        data-testid="form-clear-fecha-alta-afiliado"
                        title="Borrar fecha"
                      >
                        <X className="w-3.5 h-3.5 mr-1" />
                        Borrar
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                    Fecha de baja como afiliado
                  </Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input
                      type="date"
                      value={form.fecha_baja_afiliado || ""}
                      onChange={(e) => update({ fecha_baja_afiliado: e.target.value })}
                      className="rounded-sm border-slate-300"
                      data-testid="form-fecha-baja-input"
                    />
                    {form.fecha_baja_afiliado && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => update({ fecha_baja_afiliado: "" })}
                        className="rounded-sm border-slate-300 h-9 text-xs"
                        data-testid="form-clear-fecha-baja-afiliado"
                        title="Borrar fecha"
                      >
                        <X className="w-3.5 h-3.5 mr-1" />
                        Borrar
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-4">
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">
              Redes sociales
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Instagram">
                <Input
                  value={form.instagram}
                  onChange={(e) => update({ instagram: e.target.value })}
                  placeholder="https://instagram.com/usuario"
                  className="rounded-sm border-slate-300"
                  data-testid="form-instagram-input"
                />
              </Field>
              <Field label="Facebook">
                <Input
                  value={form.facebook}
                  onChange={(e) => update({ facebook: e.target.value })}
                  placeholder="https://facebook.com/usuario"
                  className="rounded-sm border-slate-300"
                  data-testid="form-facebook-input"
                />
              </Field>
            </div>
          </div>

          {/* Referente Territorio */}
          <div className="border-t border-slate-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.es_referente_territorio}
                onCheckedChange={(v) => update({ es_referente_territorio: !!v })}
                className="mt-0.5 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                data-testid="form-referente-territorio-checkbox"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">¿Es referente de su Territorio?</p>
              </div>
            </label>
          </div>

          {/* Voluntario + hoja alta */}
          <div className="border-t border-slate-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.es_voluntario}
                onCheckedChange={(v) => update({ es_voluntario: !!v })}
                className="mt-0.5 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                data-testid="form-voluntario-checkbox"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">Voluntari 2027</p>
              </div>
            </label>
            {form.es_voluntario && (
              <div className="ml-7 mt-3 space-y-2" data-testid="form-voluntario-section">
                <p className="text-xs text-slate-600">
                  Hoja de alta Voluntari 2027 (PDF, JPG, PNG, HEIC). Solo un archivo.
                </p>
                {voluntarioFile ? (
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid="voluntario-file-row">
                    <FileIcon ct={voluntarioFile.content_type} filename={voluntarioFile.filename} />
                    <span className="text-sm text-slate-800 truncate flex-1" title={voluntarioFile.filename}>
                      {voluntarioFile.filename}
                    </span>
                    <a
                      href={previewUrl(voluntarioFile.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-slate-900 p-1"
                      title="Ver"
                      data-testid="voluntario-file-view"
                    >
                      <Eye className="w-4 h-4" />
                    </a>
                    <a
                      href={downloadUrl(voluntarioFile.id)}
                      download={voluntarioFile.filename}
                      className="text-slate-600 hover:text-slate-900 p-1"
                      title="Descargar"
                      data-testid="voluntario-file-download"
                    >
                      <Upload className="w-4 h-4 rotate-180" />
                    </a>
                    <button
                      type="button"
                      onClick={() => removeFile(voluntarioFile.id, "voluntario")}
                      className="text-red-600 hover:text-red-700 p-1"
                      title="Eliminar"
                      data-testid="voluntario-file-delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <FileUploadRow
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                    label="Subir hoja de alta"
                    onUpload={(f) => uploadFile("voluntario", f)}
                    disabled={!contactId || uploadingKind === "voluntario"}
                    disabledMsg={!contactId ? "Guarda el contacto primero" : null}
                    testId="voluntario-file-upload"
                  />
                )}

                {/* Disponibilitat horària */}
                <div className="pt-4 border-t border-slate-200" data-testid="voluntari-disponibilitat-block">
                  <p className="text-sm font-semibold text-slate-800 mb-2">Disponibilitat horària</p>
                  <div className="overflow-x-auto">
                    <table className="text-sm border border-slate-200 rounded-sm">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="px-3 py-2 text-left font-medium text-slate-700 border-b border-slate-200">Franja</th>
                          {DIES_SEMANA.map((d) => (
                            <th key={d.key} className="px-3 py-2 text-center font-medium text-slate-700 border-b border-slate-200 w-12">
                              {d.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {FRANJAS_HORARIAS.map((f) => (
                          <tr key={f.key} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-800 font-medium">{f.label}</td>
                            {DIES_SEMANA.map((d) => {
                              const slot = `${f.key}_${d.key}`;
                              const checked = (form.voluntari_disponibilitat || []).includes(slot);
                              return (
                                <td key={d.key} className="px-3 py-2 text-center">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={() => toggleDisponibilitat(slot)}
                                    className="data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                                    data-testid={`voluntari-disp-${slot}`}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Observacions sobre l'horari */}
                <div className="pt-4 border-t border-slate-200">
                  <Label className="text-sm font-semibold text-slate-800">Observacions sobre l'horari</Label>
                  <Textarea
                    value={form.voluntari_horari_observacions || ""}
                    onChange={(e) => update({ voluntari_horari_observacions: e.target.value })}
                    rows={3}
                    className="mt-2 rounded-sm border-slate-300"
                    data-testid="voluntari-horari-observacions"
                  />
                </div>

                {/* Equip */}
                <div className="pt-4 border-t border-slate-200">
                  <Label className="text-sm font-semibold text-slate-800">Equip</Label>
                  <Input
                    value={form.voluntari_equip || ""}
                    onChange={(e) => update({ voluntari_equip: e.target.value })}
                    className="mt-2 rounded-sm border-slate-300"
                    data-testid="voluntari-equip"
                  />
                </div>

                {/* Voluntariat en actes */}
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm font-semibold text-slate-800 mb-2">Voluntariat en actes</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {VOLUNTARI_ACTES.map((acte) => {
                      const checked = (form.voluntari_actes || []).includes(acte);
                      return (
                        <label
                          key={acte}
                          className="flex items-start gap-2 cursor-pointer bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 hover:bg-slate-100"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleActe(acte)}
                            className="mt-0.5 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                            data-testid={`voluntari-acte-${acte.substring(0, 20).replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                          />
                          <span className="text-sm text-slate-800">{acte}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Consell municipal sectorial */}
          <div className="border-t border-slate-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.pertenece_consell_sectorial}
                onCheckedChange={(v) => update({ pertenece_consell_sectorial: !!v })}
                className="mt-0.5 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                data-testid="form-consell-sectorial-checkbox"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">¿Pertenece al consell municipal sectorial?</p>
              </div>
            </label>
            {form.pertenece_consell_sectorial && (
              <div className="ml-7 mt-3 space-y-1" data-testid="form-consells-sectorials-section">
                <p className="text-xs text-slate-600 mb-1.5">Selecciona uno o varios:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
                  {CONSELLS_SECTORIALS.map((c) => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer px-2 py-1 hover:bg-slate-50 rounded-sm">
                      <Checkbox
                        checked={form.consells_sectorials?.includes(c)}
                        onCheckedChange={() => toggleConsellSectorial(c)}
                        className="data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                        data-testid={`form-consell-sect-${c}`}
                      />
                      <span className="text-xs text-slate-800">{c}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Consell municipal de districte */}
          <div className="border-t border-slate-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.pertenece_consell_districte}
                onCheckedChange={(v) => update({ pertenece_consell_districte: !!v })}
                className="mt-0.5 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                data-testid="form-consell-districte-checkbox"
              />
              <div>
                <p className="text-sm font-semibold text-slate-800">¿Pertenece al consell municipal de districte?</p>
              </div>
            </label>
            {form.pertenece_consell_districte && (
              <div className="ml-7 mt-3" data-testid="form-consells-districte-section">
                <p className="text-xs text-slate-600 mb-1.5">Selecciona uno o varios:</p>
                <div className="flex flex-wrap gap-2">
                  {CONSELLS_DISTRICTE.map((d) => {
                    const checked = form.consells_districte?.includes(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => toggleConsellDistricte(d)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                          checked
                            ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                        }`}
                        data-testid={`form-consell-districte-${d}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Documentos legales */}
          <div className="border-t border-slate-200 pt-4">
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">
              Documentos legales
            </p>
            <p className="text-xs text-slate-600 mb-3">
              Escaneos, contratos o cualquier documento (PDF, JPG, PNG, HEIC). Sin límite de cantidad.
            </p>
            <div className="space-y-2" data-testid="docs-legales-list">
              {docsLegales.map((f) => (
                <div key={f.id} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid={`legal-doc-${f.id}`}>
                  <FileIcon ct={f.content_type} filename={f.filename} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-800 truncate" title={f.filename}>{f.filename}</p>
                    <Input
                      defaultValue={f.description || ""}
                      onBlur={(e) => {
                        if (e.target.value !== (f.description || "")) {
                          updateDocDescription(f.id, e.target.value);
                        }
                      }}
                      placeholder="Descripción..."
                      className="rounded-sm border-slate-300 h-8 text-xs mt-1"
                      data-testid={`legal-doc-desc-${f.id}`}
                    />
                  </div>
                  <div className="flex gap-1 self-end sm:self-auto">
                    <a href={previewUrl(f.id)} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-slate-900 p-1" title="Ver">
                      <Eye className="w-4 h-4" />
                    </a>
                    <a href={downloadUrl(f.id)} download={f.filename} className="text-slate-600 hover:text-slate-900 p-1" title="Descargar">
                      <Upload className="w-4 h-4 rotate-180" />
                    </a>
                    <button type="button" onClick={() => removeFile(f.id, "legal")} className="text-red-600 hover:text-red-700 p-1" title="Eliminar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <FileUploadRow
                accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                label="Añadir documento legal"
                onUpload={(f) => uploadFile("legal", f)}
                disabled={!contactId || uploadingKind === "legal"}
                disabledMsg={!contactId ? "Guarda el contacto primero para subir documentos" : null}
                testId="legal-doc-upload"
                multiple
              />
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-1.5 block">
              Comentaris
            </Label>
            <Textarea
              value={form.comentaris || ""}
              onChange={(e) => update({ comentaris: e.target.value })}
              placeholder="Notas internas, observaciones, etc."
              rows={4}
              className="rounded-sm border-slate-300"
              data-testid="form-comentaris-input"
            />
          </div>

          {error && (
            <div
              className="bg-red-50 border-l-2 border-red-600 px-3 py-2 text-sm text-red-700"
              data-testid="form-error-message"
            >
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-sm border-slate-300"
              data-testid="form-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="form-submit-button"
            >
              {saving ? "Guardando..." : initial ? "Guardar cambios" : "Crear contacto"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
      {label}
    </Label>
    {children}
  </div>
);

const BoolField = ({ label, checked, onChange, disabled, testid, hint }) => (
  <label
    className={`flex items-start gap-3 p-3 border border-slate-200 bg-white cursor-pointer rounded-sm transition-colors duration-150 ${
      disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : "hover:bg-slate-50"
    }`}
  >
    <Checkbox
      checked={checked}
      onCheckedChange={(v) => onChange(!!v)}
      disabled={disabled}
      className="mt-0.5 data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
      data-testid={testid}
    />
    <div className="flex-1">
      <p className="text-sm text-slate-900 leading-tight">{label}</p>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
  </label>
);


const FileIcon = ({ ct, filename }) => {
  const ext = (filename || "").split(".").pop()?.toLowerCase();
  if (ct?.startsWith("image/") || ["jpg", "jpeg", "png", "heic", "heif"].includes(ext || "")) {
    return <ImageIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />;
  }
  return <FileText className="w-5 h-5 text-slate-500 flex-shrink-0" />;
};

const FileUploadRow = ({ accept, label, onUpload, disabled, disabledMsg, testId, multiple = false }) => {
  const inputRef = React.useRef(null);
  const handleChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (multiple) {
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        await onUpload(f);
      }
    } else if (files[0]) {
      await onUpload(files[0]);
    }
    if (inputRef.current) inputRef.current.value = "";
  };
  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className={`inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-sm text-sm ${
          disabled ? "opacity-50 cursor-not-allowed bg-slate-50" : "bg-white hover:bg-slate-50"
        }`}
        data-testid={testId}
        title={disabledMsg || undefined}
      >
        <Upload className="w-4 h-4" />
        {label}
      </button>
      {disabledMsg && (
        <p className="text-xs text-amber-700 mt-1">{disabledMsg}</p>
      )}
    </div>
  );
};
